/**
 * Mapping utilities between PrLiteratureReviewSidebar options and URL paths.
 *
 * Each sidebar option corresponds to a unique route under /pr-literature-review/.
 * These helpers convert between the `SidebarOption` union type and the URL
 * pathname so the sidebar selection is always reflected in the browser URL.
 *
 * Routes:
 *   - Overview (default)    → /pr-literature-review
 *   - All PR Articles       → /pr-literature-review/all-pr-articles
 *   - Open Author Contacts  → /pr-literature-review/open-author-contacts
 *   - Needs Rereview        → /pr-literature-review/needs-rereview
 *   - New Finds             → /pr-literature-review/new-finds
 *   - GBD Cycle (PR coll.)  → /pr-literature-review/cycle/:id
 */

import {
  SidebarOption,
  DEFAULT_SIDEBAR_OPTION,
  collectionIdFromOption,
} from "@/components/pr-literature-review/PrLiteratureReviewSidebar"

/** Base path for the PR literature review section. */
const BASE_PATH = "/pr-literature-review"

/**
 * Static sidebar options that map 1:1 to a URL segment under
 * /pr-literature-review/. The default option ("overview") maps to the bare base
 * path.
 */
const STATIC_OPTION_SEGMENTS: Record<string, SidebarOption> = {
  "all-pr-articles": "all-pr-articles",
  "open-author-contacts": "open-author-contacts",
  "needs-rereview": "needs-rereview",
  "new-finds": "new-finds",
}

/**
 * Converts a SidebarOption into the corresponding URL path.
 *
 * @param option - The sidebar option to convert.
 * @returns The absolute URL path (e.g. "/pr-literature-review/cycle/42").
 */
export function sidebarOptionToPath(option: SidebarOption): string {
  // Default option maps to the bare base path.
  if (option === DEFAULT_SIDEBAR_OPTION) {
    return BASE_PATH
  }

  // Static options (all-pr-articles, open-author-contacts, needs-rereview,
  // new-finds).
  if (option in STATIC_OPTION_SEGMENTS) {
    return `${BASE_PATH}/${option}`
  }

  // Cycle options → /pr-literature-review/cycle/:id.
  const collectionId = collectionIdFromOption(option)
  if (collectionId !== null) {
    return `${BASE_PATH}/cycle/${collectionId}`
  }

  // Fallback — should not happen with a well-typed SidebarOption.
  return BASE_PATH
}

/**
 * Derives the current SidebarOption from the browser's location pathname.
 *
 * @param pathname - The current location.pathname.
 * @returns The resolved SidebarOption, defaulting to "overview" if
 *   unrecognized.
 */
export function pathToSidebarOption(pathname: string): SidebarOption {
  // Strip trailing slash for consistency.
  const normalized = pathname.endsWith("/") ? pathname.slice(0, -1) : pathname

  // Bare /pr-literature-review → default (overview).
  if (normalized === BASE_PATH) {
    return DEFAULT_SIDEBAR_OPTION
  }

  // /pr-literature-review/cycle/:id
  const cycleMatch = normalized.match(/^\/pr-literature-review\/cycle\/(\d+)$/)
  if (cycleMatch) {
    return `collection-${parseInt(cycleMatch[1], 10)}` as SidebarOption
  }

  // /pr-literature-review/:segment — check static options.
  const segmentMatch = normalized.match(/^\/pr-literature-review\/(.+)$/)
  if (segmentMatch) {
    const segment = segmentMatch[1]
    if (segment in STATIC_OPTION_SEGMENTS) {
      return STATIC_OPTION_SEGMENTS[segment]
    }
  }

  return DEFAULT_SIDEBAR_OPTION
}
